import { Form, Input, message, Select } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { MakeApiCall } from "../../../apis";
import { DefaultAdminPage, DefaultUserPage, Types } from "../../../config";
import { setData } from "../../../redux/actions";
const WrapperSignIn = styled.div`
  background-color: #F1D5D9;
  .ant-input-affix-wrapper .ant-input-suffix {
    position: absolute;
    right: 20px;
    top: 20px;
  }
  .ant-input-affix-wrapper .ant-input{
    font-size: 1.1rem;
    font-weight: 500;
    line-height: 2.5;
  }
  .ant-input-affix-wrapper .ant-input::placeholder {
    color: #A1A5B7;
  }
  .ant-form-item .ant-form-item-explain-error {
    color: #ff4d4f;
    width: 100%;
    margin-top: .5rem;
    font-size: .95rem;
    color: var(--bs-danger-text);
  }
  .ant-form-item {
    margin-bottom: 13px;
  }
`;

const findRoutes = {
  superAdmin: DefaultAdminPage,
  admin: DefaultAdminPage,
  manager: DefaultAdminPage,
  user: DefaultUserPage,
};

// Common country codes for the selector
const COUNTRY_CODES = [
  { label: "+91 🇮🇳", value: "+91" },
  { label: "+1 🇺🇸",  value: "+1"  },
  { label: "+44 🇬🇧", value: "+44" },
  { label: "+61 🇦🇺", value: "+61" },
  { label: "+971 🇦🇪", value: "+971" },
  { label: "+65 🇸🇬", value: "+65" },
  { label: "+60 🇲🇾", value: "+60" },
];

export default function SignUp() {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.data);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Check if token exists in URL
  const verificationToken = searchParams.get("token");
  const hasToken = !!verificationToken;

  const handleLogin = (data) => {
    const type = Types?.[data?.data?.user_data?.user_type];
    const obj = {
      user: data?.data,
      loginType: type,
      token: data?.data?.auth_token_data?.access_token,
    };
    dispatch(setData(obj));
    navigate(findRoutes?.[type]);
  };

  useEffect(() => {
    if (Object.keys(user)?.length !== 0) {
      setLoading(false);
      navigate(findRoutes?.[user.loginType]);
    }
    return () => {};
  }, []);

  const onFinish = async (values) => {
    setSubmitLoading(true);

    try {
      if (!hasToken) {
        // Step 1: Send email validation — include contact_no here
        const contact_no = `${values.country_code}${values.phone_number}`;

        const response = await MakeApiCall("send-email-validation", "post", {
          email: values.email,
          contact_no,
        });

        if (response?.status === true) {
          const successMsg = response?.message || "Verification email sent! Please check your inbox.";
          message.success(successMsg);
          setSuccessMessage(successMsg);
          setSubmitLoading(false);
          setTimeout(() => setSuccessMessage(""), 5000);
        } else {
          setSubmitLoading(false);
          message.error(response?.message || "Failed to send verification email.");
        }
      } else {
        // Step 2: Complete sign-up — no contact_no needed here
        const response = await MakeApiCall("sign-up", "post", {
          name: values.name,
          password: values.password,
          confirm_password: values.confirm_password,
          verification_token: verificationToken,
          user_type: 2,
        });

        if (response?.status === true) {
          const successMsg = response?.message || "Account created successfully! You can now sign in.";
          message.success(successMsg);
          handleLogin(response);
        } else {
          setSubmitLoading(false);
          message.error(response?.message || "Failed to complete sign-up.");
        }
      }
    } catch (error) {
      setSubmitLoading(false);
      const errorMessage = error?.response?.data?.message || error?.message || "An error occurred.";
      message.error(errorMessage);
    }
  };

  return (
      <WrapperSignIn  className=" h-100">
        <div className="d-flex flex-column flex-lg-row flex-column-fluid h-100" style={{backgroundImage: 'url(assets/media/aerial-view-business-team.jpg)', backgroundSize: 'cover'}}>
          {/*begin::Body*/}
          <div className="d-flex flex-column flex-lg-row-fluid w-lg-40 p-10 order-2 order-lg-1 bg-white m-8" style={{borderRadius: 20}}>
            {/*begin::Form*/}
            <div className="d-flex flex-center flex-column flex-lg-row-fluid">
              {/*begin::Wrapper*/}
              <div className="w-lg-500px p-10 px-15 pb-6 py-10 p-sign-0 pt-0">
                <a href="index.html" className="mb-0 mb-lg-5  d-flex  text-start">
                  <img alt="Logo" src="assets/media/SellerViewLogo.png" className="w-150px ms-n7" />
                </a>
                {/*begin::Form*/}
                <Form
                    name="register"
                    initialValues={{ remember: true, country_code: "+91" }}
                    className="form w-100"
                    onFinish={onFinish}
                    form={form}
                >
                  {/*begin::Heading*/}
                  <div className="text-start mb-5">
                    {/*begin::Title*/}
                    <h1 className="text-gray-900 fw-bolder mb-3">
                      {hasToken ? "Create your account" : "Sign Up"}
                    </h1>
                    {/*end::Title*/}
                    {/*begin::Subtitle*/}
                    <div className="text-gray-500 fw-semibold fs-5">
                      {hasToken ? "Please enter details below" : "Enter your email to get started"}
                    </div>
                    {/*end::Subtitle=*/}
                  </div>
                  {/*begin::Heading*/}

                  {/* Success Message */}
                  {successMessage && (
                      <div className="mb-5 p-4 bg-success bg-opacity-10 border border-success rounded">
                        <div className="text-success fw-semibold fs-6">
                          <i className="ki-outline ki-check-circle fs-2 me-2" />
                          {successMessage}
                        </div>
                      </div>
                  )}

                  {/* Show Name field only if token exists */}
                  {hasToken && (
                      <div className="fv-row mb-0">
                        {/*begin::Name*/}
                        <Form.Item
                            name="name"
                            rules={[
                              {
                                required: true,
                                message: "Please input your name!",
                              },
                            ]}
                        >
                          <Input
                              size="large"
                              className="form-control form-control-solid px-8 h-55px "
                              variant="filled"
                              placeholder="Name"
                          />
                        </Form.Item>
                        {/*end::Name*/}
                      </div>
                  )}

                  {/*begin::Input group=*/}
                  <div className="fv-row mb-5">
                    {/* Step 1: email + phone | Step 2: only email (read-only, hidden) */}
                    {!hasToken ? (
                        <>
                          {/*begin::Email*/}
                          <Form.Item
                              name="email"
                              rules={[
                                {
                                  type: "email",
                                  message: "The input is not valid E-mail!",
                                },
                                {
                                  required: true,
                                  message: "Please input your E-mail!",
                                },
                              ]}
                          >
                            <Input
                                size="large"
                                className="form-control form-control-solid px-8 h-55px "
                                variant="filled"
                                placeholder="Email"
                            />
                          </Form.Item>
                          {/*end::Email*/}

                          {/*begin::Mobile Number*/}
                          <Form.Item style={{ marginBottom: 0 }}>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <Form.Item
                                  name="country_code"
                                  noStyle
                                  rules={[{ required: true, message: "Select country code" }]}
                              >
                                <Select
                                    size="large"
                                    variant="filled"
                                    style={{ width: 110, height: 55 }}
                                    options={COUNTRY_CODES}
                                    popupMatchSelectWidth={false}
                                />
                              </Form.Item>
                              <Form.Item
                                  name="phone_number"
                                  noStyle
                                  rules={[
                                    { required: true, message: "Please input your mobile number!" },
                                    { pattern: /^\d{7,15}$/, message: "Enter a valid mobile number!" },
                                  ]}
                              >
                                <Input
                                    size="large"
                                    className="form-control form-control-solid px-8 h-55px"
                                    variant="filled"
                                    placeholder="Mobile Number"
                                    style={{ flex: 1, width: '100%' }}
                                />
                              </Form.Item>
                            </div>
                          </Form.Item>
                          {/*end::Mobile Number*/}
                        </>
                    ) : (
                        /*begin::Email (token step — hidden, no input needed)*/
                        null
                        /*end::Email*/
                    )}
                  </div>
                  {/*end::Input group=*/}

                  {/* Show Password fields only if token exists */}
                  {hasToken && (
                      <>
                        <div className="fv-row mb-5">
                          <Form.Item
                              name="password"
                              rules={[
                                {
                                  required: true,
                                  message: "Please input your password!",
                                },
                              ]}
                          >
                            <Input.Password
                                size="large"
                                className="form-control form-control-solid px-8 h-55px "
                                variant="filled"
                                placeholder="Password"
                            />
                          </Form.Item>
                        </div>
                        <div className="fv-row mb-5">
                          <Form.Item
                              name="confirm_password"
                              dependencies={["password"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Please confirm your password!",
                                },
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    if (!value || getFieldValue("password") === value) {
                                      return Promise.resolve();
                                    }
                                    return Promise.reject(
                                        new Error("The two passwords do not match!")
                                    );
                                  },
                                }),
                              ]}
                          >
                            <Input.Password
                                size="large"
                                className="form-control form-control-solid px-8 h-55px "
                                variant="filled"
                                placeholder="Confirm Password"
                            />
                          </Form.Item>
                        </div>
                      </>
                  )}

                  {/*begin::Submit button*/}
                  <div className="d-grid mb-5">
                    <button
                        type="submit"
                        id=""
                        className="btn btn-danger h-55px cus-shadow-btn"
                        disabled={submitLoading}
                        data-kt-indicator={submitLoading ? "on" : "off"}
                    >
                      {/*begin::Indicator label*/}
                      <span className="indicator-label d-flex justify-content-center">
                {hasToken ? "Sign Up" : "Send Verification Email"}
                        <i className="ki-outline ms-2 ki-arrow-right fs-1" />
              </span>
                      {/*end::Indicator label*/}
                      {/*begin::Indicator progress*/}
                      <span className="indicator-progress">
                Please wait...
                <span className="spinner-border spinner-border-sm align-middle ms-2" />
              </span>
                      {/*end::Indicator progress*/}
                    </button>
                  </div>
                  {/*end::Submit button*/}
                  {/*begin::Sign up*/}
                  <div className="text-gray-500 text-start fw-semibold fs-5">Already have an Account?
                    <Link to="/sign-in" className="link-primary"> Sign In</Link></div>
                  {/*end::Sign up*/}
                </Form>
                {/*end::Form*/}
              </div>
              {/*end::Wrapper*/}
            </div>
            {/*end::Form*/}
          </div>
          {/*end::Body*/}
          {/*begin::Aside*/}
          <div className="im-section d-flex flex-lg-row-fluid w-lg-50 bgi-size-cover bgi-position-center order-1 order-lg-2 position-relative" style={{BackgroundImage: 'url(assets/media/misc/auth-bg.png)', Background: '#ffffffd9', BackdropFilter: 'blur(6px)'}}>
            {/*begin::Content*/}
            <div className="d-flex flex-column flex-center py-7 py-lg-15 px-5 px-md-15 w-100">
              {/*begin::Image*/}
              <img className="d-none d-lg-block mx-auto w-275px w-md-50 w-xl-500px mb-10 mb-lg-10" src="assets/media/login-img.png" alt />
              {/*end::Image*/}
            </div>
            {/*end::Content*/}
          </div>
          {/*end::Aside*/}
        </div>


      </WrapperSignIn>
  );
}